// src/modules/tenant/ProductFormPage.tsx
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_ROOT_URL } from "../../config/api";
import { returnWithCreated, type CreateFlowState } from "../assetManagement/createReturn";
import initialMockData from "./mockProducts.json";

const THEME_PRIMARY = "var(--color-primary, #059669)";
const THEME_PRIMARY_SOFT = "color-mix(in srgb, var(--color-primary, #059669) 12%, white)";
const THEME_PRIMARY_RING = "color-mix(in srgb, var(--color-primary, #059669) 45%, transparent)";

const authenticatedJsonHeaders = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`, });

// --- Global Breadcrumb Portal Slot ---
const APP_BREADCRUMB_SLOT_ID = "app-breadcrumb-slot";

function useAppBreadcrumbSlot(): HTMLElement | null {
    const [slot, setSlot] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setSlot(document.getElementById(APP_BREADCRUMB_SLOT_ID));
    }, []);

    return slot;
}

// --- Inline Icons ---
const IconFile = () => (
    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
);
const IconCalendar = () => (
    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
);
const IconLink = () => (
    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
);
const IconBox = () => (
    <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
);
const IconChevronRight = () => (
    <svg className="w-4 h-4 text-gray-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
);

const ProductFormPage: React.FC = () => {
    const navigate = useNavigate();
    const handleAddNewAsset = () => { navigate("/customer-manager/asset-management?tab=upload"); };
    const { id } = useParams();
    const location = useLocation();
    const isEditMode = Boolean(id);
    const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
    const tenantId = loggedInUser.tenantId;
    const userId = loggedInUser.id;
    // Where to go after saving. Kept in state because router state is replaced on a round trip
    // to Create Event (it comes back holding only the product draft, which carries these along).
    const [origin] = useState(() => {
        const s = location.state as (CreateFlowState & { productDraft?: { origin?: CreateFlowState } }) | null;
        return {
            returnPath: s?.returnPath ?? s?.productDraft?.origin?.returnPath,
            returnTo: s?.returnTo ?? s?.productDraft?.origin?.returnTo,
        };
    });
    const returnPath = origin.returnPath || "/tenant/dashboard?tab=products-only";

    // Opened from another page (e.g. Upload assets): return there with the new product
    const returnTo = origin.returnTo;

    const eventForm = location.state?.eventForm;

    const breadcrumbSlot = useAppBreadcrumbSlot();

    // From an event: go back to its Create / Edit page with the form restored.
    // Otherwise: go to the normal return path (preserving the `returnTo` flow when present).
    const goBackToEventForm = () => {
        if (eventForm) {
            navigate(returnPath, {
                replace: true,
                state: { reopenEventForm: eventForm },
            });
            return;
        }

        if (returnTo) {
            // Opened from another page (e.g. Upload assets) — go back there.
            returnWithCreated(navigate, returnTo, {});
            return;
        }

        navigate(returnPath);
    };

    const handleCreateEventFromProduct = () => {
        setShowEventDropdown(false);

        navigate("/customer-manager/library/events", {
            state: {
                createEventFromProduct: true,
                fromProduct: {
                    returnPath: location.pathname + location.search,
                    productDraft: {
                        productFormData,
                        productTagsInput,
                        imagePreview,
                        selectedEventIds,
                        selectedAssetIds,
                        origin: { returnTo, returnPath },
                    },
                },
            },
        });
    };

    const [saving, setSaving] = useState(false);
    const [productTagsInput, setProductTagsInput] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const imageInputRef = useRef<HTMLInputElement>(null);

    // Autocomplete states
    const [showEventDropdown, setShowEventDropdown] = useState(false);
    const [eventSearch, setEventSearch] = useState("");
    const [showAssetDropdown, setShowAssetDropdown] = useState(false);
    const [assetSearch, setAssetSearch] = useState("");

    // Available Options (from Mock JSON)
    const [availableEvents, setAvailableEvents] = useState<any[]>([]);
    const [availableAssets, setAvailableAssets] = useState<any[]>([]);
    const [selectedEventIds, setSelectedEventIds] = useState<string[]>([]);
    const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);

    // Form State
    const [productFormData, setProductFormData] = useState<any>({
        name: "",
        description: "",
        launchDate: "",
        endDate: "",
        notes: "",
        tags: [],
        status: "Active",
        image: "",
        video: "",
        events: [],
        assets: [],
    });

    // Restore Product form after returning from Create Event
    useEffect(() => {
        const draft = location.state?.productDraft;
        if (!draft) return;

        setProductFormData(draft.productFormData);
        setProductTagsInput(draft.productTagsInput || "");
        setImagePreview(draft.imagePreview || "");
        setSelectedEventIds(draft.selectedEventIds || []);
        setSelectedAssetIds(draft.selectedAssetIds || []);

        const createdEvent = location.state?.createdEvent;

        if (createdEvent) {
            const eventId = String(createdEvent.id);

            const normalizedEvent = {
                ...createdEvent,
                id: eventId,
                eventName: createdEvent.eventName || createdEvent.name || "New Event",
                // The event chips and dropdown show `name`
                name: createdEvent.eventName || createdEvent.name || "New Event",
            };

            // Add the new event to the available dropdown options.
            setAvailableEvents((prev: any[]) => [
                normalizedEvent,
                ...prev.filter((event) => String(event.id) !== eventId),
            ]);

            // Automatically select it so the chip appears.
            setSelectedEventIds((prev: string[]) =>
                prev.includes(eventId) ? prev : [...prev, eventId]
            );

            setProductFormData((prev: any) => ({
                ...prev,
                events: [
                    ...(prev.events || []).filter(
                        (event: any) => String(event.id) !== eventId
                    ),
                    normalizedEvent,
                ],
            }));
        }

        // Clear navigation state after restoring it
        navigate(location.pathname + location.search, {
            replace: true,
            state: null,
        });
    }, [location.key]);

    // --- MOCK DATA MANAGEMENT ---
    const getMockData = () => {
        const data = localStorage.getItem("mockProducts");
        if (!data) {
            localStorage.setItem("mockProducts", JSON.stringify(initialMockData));
            return initialMockData;
        }
        return JSON.parse(data);
    };

    // Fetch existing product if in Edit mode + load available options
    useEffect(() => {
        let cancelled = false;

        // Keep existing event and asset dropdown options for now.
        setAvailableEvents(initialMockData.availableEvents || []);
        setAvailableAssets([]);

        const loadProduct = async () => {
            if (!isEditMode || !id) return;

            try {
                const response = await fetch(
                    `${API_ROOT_URL}/products/${encodeURIComponent(id)}`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(
                        result.message || "Failed to load product."
                    );
                }

                const product = result.data;

                if (!product || !product.product_id) {
                    throw new Error("Product not found.");
                }

                if (cancelled) return;

                const mappedProduct = {
                    id: String(product.product_id),
                    name: product.title || "",
                    description: product.description || "",
                    notes: product.notes || "",
                    launchDate: product.start_date
                        ? product.start_date.split("T")[0]
                        : "",
                    endDate: product.end_date
                        ? product.end_date.split("T")[0]
                        : "",
                    tags: Array.isArray(product.tags) ? product.tags : [],
                    status: product.status || "Active",
                    image: product.image_url || "",
                    video: product.video_url || "",
                    events: [],
                    assets: [],
                };

                setProductFormData(mappedProduct);
                setProductTagsInput(mappedProduct.tags.join(", "));
                setImagePreview(mappedProduct.image);
                // Load previously linked assets
                const assetResponse = await fetch(
                    `${API_ROOT_URL}/product-assets/${encodeURIComponent(id!)}`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const assetResult = await assetResponse.json();

                if (!assetResponse.ok || assetResult.success === false) {
                    throw new Error(assetResult.message || "Failed to load linked assets.");
                }

                const linkedAssets = Array.isArray(assetResult.data)
                    ? assetResult.data
                    : [];

                setProductFormData((prev: any) => ({
                    ...prev,
                    assets: linkedAssets.map((asset: any) => ({
                        id: String(asset.asset_id ?? asset.id),
                        name: asset.original_filename || asset.filename || asset.name || "Untitled Asset",
                    })),
                }));

                // Load previously linked events
                const eventResponse = await fetch(
                    `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events/products/${encodeURIComponent(id!)}/events`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const eventResult = await eventResponse.json();

                if (!eventResponse.ok || eventResult.success === false) {
                    throw new Error(eventResult.message || "Failed to load linked events.");
                }

                const linkedEvents = Array.isArray(eventResult.data)
                    ? eventResult.data
                    : [];

                setProductFormData((prev: any) => ({
                    ...prev,
                    events: linkedEvents.map((event: any) => ({
                        id: String(event.event_id),
                        name: event.title || "Untitled Event",
                    })),
                }));
            } catch (error: any) {
                if (!cancelled) {
                    console.error("Failed to load product:", error);
                    toast.error(error.message || "Unable to load product.");
                }
            }
        };

        loadProduct();

        return () => {
            cancelled = true;
        };
    }, [isEditMode, id]);


    // Fetch real events from the backend
    useEffect(() => {
        const fetchAvailableEvents = async () => {
            try {
                if (!tenantId) {
                    toast.error("Tenant ID is missing. Please log in again.");
                    return;
                }

                const response = await fetch(
                    `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events?tenant_id=${tenantId}&page=1&limit=100`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const result = await response.json();

                if (!response.ok || !result.success) {
                    throw new Error(result.message || "Failed to fetch events.");
                }

                const events = Array.isArray(result.data) ? result.data : [];

                // Convert backend event fields into the format used by the UI
                const formattedEvents = events
                    .filter((event: any) =>
                        String(event.status || "").toUpperCase() !== "INACTIVE"
                    )
                    .map((event: any) => ({
                        id: String(event.event_id),
                        name: event.title || "Untitled Event",
                        status: event.status,
                        startDate: event.start_date,
                        endDate: event.end_date,
                    }));

                setAvailableEvents(formattedEvents);
            } catch (error: any) {
                console.error("Fetch events error:", error);
                toast.error(error.message || "Unable to load events.");
            }
        };

        fetchAvailableEvents();
    }, [tenantId]);


    // Fetch real assets from the backend
    useEffect(() => {
        let cancelled = false;

        const fetchAvailableAssets = async () => {
            try {
                if (!tenantId) {
                    toast.error("Tenant ID is missing. Please log in again.");
                    return;
                }

                const response = await fetch(
                    `${API_ROOT_URL}/assets?tenant_id=${encodeURIComponent(tenantId)}&limit=200&skip_preview=true`,
                    {
                        method: "GET",
                        headers: authenticatedJsonHeaders(),
                    }
                );

                const result = await response.json();

                if (!response.ok || result.success === false) {
                    throw new Error(result.message || "Failed to fetch assets.");
                }

                const assets = Array.isArray(result.data)
                    ? result.data
                    : Array.isArray(result)
                        ? result
                        : [];

                const formattedAssets = assets.map((asset: any) => ({
                    id: String(asset.asset_id ?? asset.id),
                    name:
                        asset.original_filename ||
                        asset.filename ||
                        asset.name ||
                        "Untitled Asset",
                    status: asset.status,
                    container: asset.container,
                }));

                if (!cancelled) {
                    setAvailableAssets(formattedAssets);
                }
            } catch (error: any) {
                console.error("Fetch assets error:", error);

                if (!cancelled) {
                    setAvailableAssets([]);
                    toast.error(error.message || "Unable to load assets.");
                }
            }
        };

        fetchAvailableAssets();

        return () => {
            cancelled = true;
        };
    }, [tenantId]);

    // Click outside handler to close dropdowns
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.event-dropdown-container')) {
                setShowEventDropdown(false);
            }
            if (!target.closest('.asset-dropdown-container')) {
                setShowAssetDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSave = async () => {
        if (!productFormData.name?.trim()) {
            toast.warning("Please enter a product name.");
            return;
        }

        if (
            productFormData.launchDate &&
            productFormData.endDate &&
            productFormData.endDate < productFormData.launchDate
        ) {
            toast.warning("End date cannot be before the launch date.");
            return;
        }

        if (!tenantId || !userId) {
            toast.error("User or tenant information is missing. Please log in again.");
            return;
        }

        setSaving(true);

        try {
            const payload = {
                title: productFormData.name.trim(),
                description: productFormData.description || "",
                notes: productFormData.notes || "",
                start_date: productFormData.launchDate
                    ? new Date(productFormData.launchDate).toISOString()
                    : new Date().toISOString(),
                end_date: productFormData.endDate
                    ? new Date(productFormData.endDate).toISOString()
                    : null,
                status: productFormData.status || "Active",
                tags: productFormData.tags || [],
                tenant_id: tenantId,
                updated_by: userId,
                ...(!isEditMode && { created_by: userId }),
            };

            const url = isEditMode
                ? `${API_ROOT_URL}/products/${encodeURIComponent(id!)}`
                : `${API_ROOT_URL}/products`;

            const response = await fetch(url, {
                method: isEditMode ? "PUT" : "POST",
                headers: authenticatedJsonHeaders(),
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                    `Failed to ${isEditMode ? "update" : "create"} product.`
                );
            }

            // Get the product ID from the API response.
            // In edit mode, use the existing product ID.
            const savedProductId = isEditMode
                ? id
                : result.data?.product_id || result.product_id;

            if (!savedProductId) {
                throw new Error("Product saved, but product ID was not returned.");
            }

            // Save linked assets
            const assetResponse = await fetch(
                `${API_ROOT_URL}/product-assets/${encodeURIComponent(savedProductId)}`,
                {
                    method: "PUT",
                    headers: authenticatedJsonHeaders(),
                    body: JSON.stringify({
                        asset_ids: [
                            ...new Set([
                                ...productFormData.assets.map((asset: any) => asset.id),
                                ...selectedAssetIds,
                            ]),
                        ],
                        updated_by: userId,
                    }),
                }
            );

            const assetResult = await assetResponse.json();

            if (!assetResponse.ok || assetResult.success === false) {
                throw new Error(
                    assetResult.message || "Product saved, but linking assets failed."
                );
            }

            // Save linked events
            const eventResponse = await fetch(
                `${API_ROOT_URL.replace(/\/api\/?$/, "")}/events/products/${encodeURIComponent(savedProductId)}/events`,
                {
                    method: "PUT",
                    headers: authenticatedJsonHeaders(),
                    body: JSON.stringify({
                        event_ids: [
                            ...new Set([
                                ...productFormData.events.map((event: any) => event.id),
                                ...selectedEventIds,
                            ]),
                        ],
                    }),
                }
            );

            const eventResult = await eventResponse.json();

            if (!eventResponse.ok || eventResult.success === false) {
                throw new Error(
                    eventResult.message || "Product saved, but linking events failed."
                );
            }

            toast.success(
                `Product ${isEditMode ? "updated" : "created"} successfully!`
            );

            setTimeout(() => {
                // Preserve both flows:
                // 1) Opened from another page (e.g. Upload assets) → return there with the new product.
                // 2) From an event form → go back to it (with form restored).
                // 3) Otherwise → go to the normal return path.
                if (returnTo && !isEditMode) {
                    returnWithCreated(navigate, returnTo, { createdProductId: String(savedProductId) });
                } else {
                    goBackToEventForm();
                }
            }, 800);

        } catch (error: any) {
            console.error("Product save failed:", error);
            toast.error(error.message || "An error occurred while saving the product.");
        } finally {
            setSaving(false);
        }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result as string;
                setImagePreview(base64);
                setProductFormData((prev: any) => ({ ...prev, image: base64 }));
            };
            reader.readAsDataURL(file);
        }
    };

    // --- LINKING HANDLERS ---
    const toggleSelectEvent = (id: string) => {
        setSelectedEventIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const toggleSelectAsset = (id: string) => {
        setSelectedAssetIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleBulkAddEvents = () => {
        const toAdd = availableEvents.filter(e => selectedEventIds.includes(e.id));
        const newEvents = [...productFormData.events];
        toAdd.forEach(evt => {
            if (!newEvents.find((e: any) => e.id === evt.id)) newEvents.push(evt);
        });
        setProductFormData((prev: any) => ({ ...prev, events: newEvents }));
        setSelectedEventIds([]);
        setShowEventDropdown(false);
        setEventSearch("");
    };

    const handleBulkAddAssets = () => {
        const toAdd = availableAssets.filter(a => selectedAssetIds.includes(a.id));
        const newAssets = [...productFormData.assets];
        toAdd.forEach(ast => {
            if (!newAssets.find((a: any) => a.id === ast.id)) newAssets.push(ast);
        });
        setProductFormData((prev: any) => ({ ...prev, assets: newAssets }));
        setSelectedAssetIds([]);
        setShowAssetDropdown(false);
        setAssetSearch("");
    };

    const removeEvent = (id: string) => {
        setProductFormData((prev: any) => ({ ...prev, events: prev.events.filter((e: any) => e.id !== id) }));
    };

    const removeAsset = (id: string) => {
        setProductFormData((prev: any) => ({ ...prev, assets: prev.assets.filter((a: any) => a.id !== id) }));
    };

    // Filtered lists for autocomplete
    const filteredEvents = availableEvents.filter(e =>
        e.name.toLowerCase().includes(eventSearch.toLowerCase()) &&
        !productFormData.events.find((added: any) => added.id === e.id)
    );

    const filteredAssets = availableAssets.filter(a =>
        a.name.toLowerCase().includes(assetSearch.toLowerCase()) &&
        !productFormData.assets.find((added: any) => added.id === a.id)
    );

    // --- Render Breadcrumb ---
    const renderBreadcrumb = () => {
        const nav = (
            <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1 text-sm">
                {location.state?.fromEvent ? (
                    <>
                        <button
                            type="button"
                            onClick={() => navigate(returnPath)}
                            className="text-gray-500 hover:underline truncate max-w-[200px]"
                        >
                            Events
                        </button>
                        <IconChevronRight />
                        <button
                            type="button"
                            onClick={goBackToEventForm}
                            className="text-gray-500 hover:underline truncate max-w-[200px]"
                        >
                            {location.state?.eventForm?.mode === "edit" ? "Edit Event" : "Create Event"}
                        </button>
                        <IconChevronRight />
                        <span className="font-semibold text-gray-900 truncate max-w-[240px]">
                            {isEditMode ? "Edit Product" : "Create Product"}
                        </span>
                    </>
                ) : returnTo ? (
                    <>
                        <button
                            type="button"
                            onClick={() => navigate(returnTo.path)}
                            className="text-gray-500 hover:underline truncate max-w-[200px]"
                        >
                            {returnTo.label}
                        </button>
                        <IconChevronRight />
                        <span className="font-semibold text-gray-900 truncate max-w-[240px]">
                            {isEditMode ? "Edit Product" : "Create Product"}
                        </span>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            onClick={() => navigate("/tenant/dashboard")}
                            className="text-gray-500 hover:underline truncate max-w-[200px]"
                        >
                            Dashboard
                        </button>
                        <IconChevronRight />
                        <button
                            type="button"
                            onClick={() => navigate("/tenant/dashboard?tab=products-only")}
                            className="text-gray-500 hover:underline truncate max-w-[200px]"
                        >
                            Products
                        </button>
                        <IconChevronRight />
                        <span className="font-semibold text-gray-900 truncate max-w-[240px]">
                            {isEditMode ? "Edit Product" : "Create Product"}
                        </span>
                    </>
                )}
            </nav>
        );
        if (breadcrumbSlot) return createPortal(nav, breadcrumbSlot);

        return (
            <div className="sticky top-0 z-30 px-4 py-2.5 bg-white/95 backdrop-blur-md border border-gray-200 rounded-lg shadow-sm">
                {nav}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-[#f3e8e8] font-sans pb-8">
            <ToastContainer position="top-right" autoClose={3000} theme="colored" />

            <div className="max-w-[1600px] mx-auto px-6 pt-6">

                {/* Breadcrumb - Attached below the global navigation */}
                {renderBreadcrumb()}

                {/* Product Page Heading - Same style as Create Event */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center border border-red-300 bg-white/70">
                        <IconFile />
                    </div>

                    <h1 className="text-xl font-bold text-gray-900">
                        {isEditMode ? "Edit Product" : "Create Product"}
                    </h1>
                </div>

                {/* 2-Column Form Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">

                    {/* LEFT COLUMN: Product Details */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center gap-2 mb-4">
                            <IconFile />
                            <h2 className="text-base font-bold text-gray-900">Product Details</h2>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Product Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={productFormData.name}
                                    onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all placeholder-gray-400"
                                    placeholder="e.g. Summer Collection 2024"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                                <textarea
                                    rows={4}
                                    value={productFormData.description}
                                    onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none placeholder-gray-400"
                                    placeholder="Brief product description"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Notes</label>
                                <textarea
                                    rows={4}
                                    value={productFormData.notes}
                                    onChange={(e) => setProductFormData({ ...productFormData, notes: e.target.value })}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all resize-none placeholder-gray-400"
                                    placeholder="Internal notes for your team"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
                                    <select
                                        value={productFormData.status}
                                        onChange={(e) => setProductFormData({ ...productFormData, status: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all bg-white"
                                    >
                                        <option value="Active">Active</option>
                                        <option value="Archived">Archived</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tags (comma separated)</label>
                                    <input
                                        type="text"
                                        value={productTagsInput}
                                        onChange={(e) => {
                                            setProductTagsInput(e.target.value);
                                            setProductFormData({ ...productFormData, tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean) });
                                        }}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all placeholder-gray-400"
                                        placeholder="tag1, tag2, tag3"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Schedule, Media & Linking */}
                    <div className="space-y-5">
                        {/* Schedule Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-2 mb-4">
                                <IconCalendar />
                                <h2 className="text-base font-bold text-gray-900">Schedule</h2>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Launch Date</label>
                                    <input
                                        type="date"
                                        value={productFormData.launchDate}
                                        onChange={(e) => setProductFormData({ ...productFormData, launchDate: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        value={productFormData.endDate}
                                        onChange={(e) => setProductFormData({ ...productFormData, endDate: e.target.value })}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Linking Card */}
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-5">

                            {/* Linked Events */}
                            <div>
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-2">
                                    <IconLink /> Link events (optional)
                                </label>

                                {(productFormData.events.length > 0 || selectedEventIds.length > 0) && (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {[...productFormData.events, ...availableEvents.filter((evt: any) => selectedEventIds.includes(evt.id) && !productFormData.events.some((added: any) => added.id === evt.id))].map((evt: any) => (
                                            <span key={evt.id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium">
                                                <span className="truncate max-w-[120px]">{evt.name}</span>
                                                <button type="button" onClick={() => { if (selectedEventIds.includes(evt.id)) setSelectedEventIds(prev => prev.filter(x => x !== evt.id)); removeEvent(evt.id); }} className="text-gray-400 hover:text-red-500 shrink-0 text-sm leading-none">×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="flex gap-2 relative event-dropdown-container">
                                    <div className="relative flex-1">
                                        <button type="button" onClick={() => setShowEventDropdown(!showEventDropdown)} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors">
                                            <span className="truncate">Choose related events</span>
                                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                        {showEventDropdown && (
                                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                                <div className="p-1.5 border-b border-gray-100 bg-gray-50/50">
                                                    <input type="text" placeholder="Search events..." value={eventSearch} onChange={(e) => setEventSearch(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500" autoFocus />
                                                </div>
                                                <div className="max-h-40 overflow-y-auto p-1">
                                                    {filteredEvents.length === 0 ? <p className="p-2 text-xs text-gray-500 text-center">No events found.</p> : filteredEvents.map((evt: any) => (
                                                        <label key={evt.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                                                            <input type="checkbox" checked={selectedEventIds.includes(evt.id)} onChange={() => toggleSelectEvent(evt.id)} className="rounded border-gray-300 text-red-600 focus:ring-red-500" />
                                                            <span className="text-gray-700 truncate">{evt.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleCreateEventFromProduct}
                                        className="px-2.5 py-2 rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-colors shrink-0"
                                        title="Create a new event"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            {/* Linked Assets */}
                            <div className="pt-4 border-t border-gray-100">
                                <label className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-2">
                                    <IconBox /> Link assets (optional)
                                </label>

                                {(productFormData.assets.length > 0 || selectedAssetIds.length > 0) && (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {[...productFormData.assets, ...availableAssets.filter((ast: any) => selectedAssetIds.includes(ast.id) && !productFormData.assets.some((added: any) => added.id === ast.id))].map((ast: any) => (
                                            <span key={ast.id} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-xs font-medium">
                                                <span className="truncate max-w-[120px]">{ast.name}</span>
                                                <button type="button" onClick={() => { if (selectedAssetIds.includes(ast.id)) setSelectedAssetIds(prev => prev.filter(x => x !== ast.id)); removeAsset(ast.id); }} className="text-gray-400 hover:text-red-500 shrink-0 text-sm leading-none">×</button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div className="flex gap-2 relative asset-dropdown-container">
                                    <div className="relative flex-1">
                                        <button type="button" onClick={() => setShowAssetDropdown(!showAssetDropdown)} className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-sm text-left text-gray-500 hover:border-gray-400 transition-colors">
                                            <span className="truncate">Select product assets</span>
                                            <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                        </button>
                                        {showAssetDropdown && (
                                            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                                                <div className="p-1.5 border-b border-gray-100 bg-gray-50/50">
                                                    <input type="text" placeholder="Search assets..." value={assetSearch} onChange={(e) => setAssetSearch(e.target.value)} className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500" autoFocus />
                                                </div>
                                                <div className="max-h-40 overflow-y-auto p-1">
                                                    {filteredAssets.length === 0 ? <p className="p-2 text-xs text-gray-500 text-center">No assets found.</p> : filteredAssets.map((ast: any) => (
                                                        <label key={ast.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-sm">
                                                            <input type="checkbox" checked={selectedAssetIds.includes(ast.id)} onChange={() => toggleSelectAsset(ast.id)} className="rounded border-gray-300 text-red-600 focus:ring-red-500" />
                                                            <span className="text-gray-700 truncate">{ast.name}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleAddNewAsset}
                                        className="px-2.5 py-2 rounded-lg border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 transition-colors shrink-0"
                                        title="Upload new asset"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer / Action Area */}
                <div className="mt-6 bg-white rounded-xl p-3 shadow-sm border border-gray-100 flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                        <span className="text-red-500">*</span> Required fields <span className="mx-1">•</span> You can edit these details later.
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={goBackToEventForm}
                            className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-6 py-2.5 text-white bg-red-600 rounded-xl hover:bg-red-700 disabled:opacity-70 font-medium text-sm shadow-sm flex items-center justify-center gap-2 transition-colors"
                        >
                            {saving ? "Saving..." : isEditMode ? "Update Product" : "Create Product"}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default ProductFormPage;